import React from 'react';
import { Mission, MissionCompletionData } from '../../../types/mission';
import styles from './MissionResults.module.css';
import { formatTime } from 'utils/timeUtils';
import { formatSkillLabel, getScorePerformanceBand, getStarRating, STAR_COUNT } from '../../../utils/missionDisplay';
import { normalizeMissionResults } from '../../../utils/missionResultsDisplay';

/**
 * Props for the MissionResults component
 */
interface MissionResultsProps {
  mission: Mission;
  results: MissionCompletionData;
  onRetry: () => void;
  onContinue: () => void;
}

/**
 * MissionResults component
 * 
 * Displays the results for a completed mission.
 */
const MissionResults: React.FC<MissionResultsProps> = ({
  mission,
  results: { success, score, accuracy, timeSpent, movesUsed, hintsUsed },
  onRetry,
  onContinue,
}) => {
  const normalizedResults = normalizeMissionResults({
    score,
    accuracy,
    timeSpent,
    movesUsed,
    hintsUsed,
  });
  const roundedScore = normalizedResults.score;
  const roundedAccuracy = normalizedResults.accuracy;
  const roundedTimeSpent = normalizedResults.timeSpent;
  const roundedMovesUsed = normalizedResults.movesUsed;
  const roundedHintsUsed = normalizedResults.hintsUsed;

  // --- Helper functions ---
  const starCount = getStarRating(roundedScore);

  // Render star rating
  const renderStars = () => {
    const stars = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      stars.push(
        <span 
          key={i} 
          className={`${styles.star} ${i < starCount ? styles.filled : ''}`}
          aria-hidden="true"
        >
          ★
        </span>
      );
    }
    return stars;
  };
  
  // Get score class
  const getScoreClass = (): string => {
    const band = getScorePerformanceBand(roundedScore);
    if (band === 'excellent') return styles.excellent;
    if (band === 'good') return styles.good;
    if (band === 'average') return styles.average;
    return styles.poor;
  };

  // Calculate earned rewards (only if successful)
  const earnedExperience = success ? mission.rewards.experience : 0;
  const earnedSkillPoints = success ? mission.rewards.skillPoints : {
    coordinates: 0,
    basicMoves: 0,
    sequences: 0,
    gameAnalysis: 0,
  };

  // --- Main Render --- 

  return (
    <div className={styles.container} data-testid="mission-results">
      {/* Header: Title and Stars */}
      <div className={`${styles.header} ${success ? styles.success : styles.failure}`}>
        <h1 className={styles.title}>
          {success ? 'Mission Accomplished' : 'Mission Failed'}
        </h1>
        {/* Show stars if score > 0 */}
        {roundedScore > 0 && (
            <div className={styles.stars} aria-label={`${starCount} out of ${STAR_COUNT} stars`}>
                {renderStars()}
            </div>
        )}
        {/* @TODO: Add asset: Success or failure badge/icon */}
      </div>

      {/* Mission Info */}
      <div className={styles.missionInfo}>
        <h2 className={styles.missionTitle}>{mission.title}</h2>
        <p className={styles.missionType}>
          Stage {mission.stage} | {mission.type.charAt(0).toUpperCase() + mission.type.slice(1)} Mission
        </p>
      </div>

      {/* Results Grid */}
      <div className={styles.resultsGrid}>
        {/* Score (Always show) */}
        <div className={styles.resultCard} data-testid="result-card-score">
          <div className={styles.resultIcon}>🎯</div>
          <div className={styles.resultLabel}>Mission Score</div>
          <div className={`${styles.resultValue} ${getScoreClass()}`}>{roundedScore}%</div>
        </div>

        {/* Accuracy (Always show) */}
        <div className={styles.resultCard} data-testid="result-card-accuracy">
            <div className={styles.resultIcon}>📊</div>
            <div className={styles.resultLabel}>Accuracy</div>
            <div className={styles.resultValue}>{roundedAccuracy.toFixed(1)}%</div>
        </div>

        {/* Time Spent (Always show) */}
        <div className={styles.resultCard} data-testid="result-card-time">
          <div className={styles.resultIcon}>⏱️</div>
          <div className={styles.resultLabel}>Time</div>
          <div className={styles.resultValue}>{formatTime(roundedTimeSpent)}</div>
        </div>

        {/* Moves Used (Only for 'move' missions) */}
        {mission.type === 'move' && roundedMovesUsed !== undefined && roundedMovesUsed > 0 && (
          <div className={styles.resultCard} data-testid="result-card-moves">
            <div className={styles.resultIcon}>♟️</div>
            <div className={styles.resultLabel}>Moves Used</div>
            <div className={styles.resultValue}>{roundedMovesUsed}</div>
          </div>
        )}

        {/* Hints Used (Only for 'move' missions) */}
        {mission.type === 'move' && roundedHintsUsed !== undefined && roundedHintsUsed > 0 && (
          <div className={styles.resultCard} data-testid="result-card-hints">
            <div className={styles.resultIcon}>💡</div>
            <div className={styles.resultLabel}>Hints Used</div>
            <div className={styles.resultValue}>{roundedHintsUsed}</div>
          </div>
        )}
      </div>

      {/* Rewards Section */}
      {success && (
        <div className={styles.rewardsSection}>
          <h2 className={styles.rewardsTitle}>Rewards Earned</h2>
          
          {/* Experience (Always show if successful) */}
          <div className={styles.rewardCard}>
            <div className={styles.rewardIcon}>⭐</div>
            <div className={styles.rewardInfo}>
              <div className={styles.rewardLabel}>Experience</div>
              <div className={styles.rewardValue}>+{earnedExperience} XP</div>
            </div>
          </div>

          {/* Skill Points (Always show if successful) */}
          {Object.entries(earnedSkillPoints).map(([skill, points]) => (
            points > 0 && (
              <div key={skill} className={styles.rewardCard}>
                <div className={styles.rewardIcon}>
                  {skill === 'coordinates' ? '🎯' : 
                   skill === 'basicMoves' ? '♟️' : 
                   skill === 'sequences' ? '📋' : '🧠'}
                </div>
                <div className={styles.rewardInfo}>
                  <div className={styles.rewardLabel}>
                    {formatSkillLabel(skill)}
                  </div>
                  <div className={styles.rewardValue}>+{points} points</div>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Feedback Section */}
      <div className={styles.feedback}>
        {success ? (
          <p className={styles.successMessage}>
            Well done, agent! Mission Accomplished.
            {getScorePerformanceBand(roundedScore) === 'excellent' && ' Outstanding performance!'}
          </p>
        ) : (
          <p className={styles.failureMessage}>
            Mission Failed. Better luck next time, agent.
          </p>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <button 
          className={`${styles.actionButton} ${styles.retryButton}`}
          onClick={onRetry}
          aria-label="Retry mission"
          data-testid="retry-mission-button"
        >
          Try Again
        </button>
        <button 
          className={`${styles.actionButton} ${styles.continueButton}`}
          onClick={onContinue}
          aria-label={success ? "Continue" : "Return to Dashboard"}
          data-testid="continue-button"
        >
          {success ? 'Continue' : 'Return to HQ'}
        </button>
      </div>
    </div>
  );
};

export default MissionResults; 
